import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useQuote } from "@/context/QuoteContext";
import { submitQuote } from "@/lib/api";
import { BUYER_TYPES } from "@/data/site";
import { EASE } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-cream/20 bg-transparent px-4 py-2.5 text-sm text-cream outline-none placeholder:text-cream/40 focus:border-kwila";

export default function QuoteDrawer() {
  const { items, open, setOpen, setQty, remove, clear } = useQuote();
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", buyer_type: BUYER_TYPES[0], message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitQuote({
        ...form,
        items: items.map((i) => ({ product_id: i.id, name: i.name, qty: i.qty })),
      });
      setDone(true);
      clear();
      toast.success("Quote request sent", { description: "Our team will respond with pricing and freight options." });
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => setDone(false), 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm"
            data-testid="quote-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-ink text-cream"
            data-testid="quote-drawer"
          >
            <div className="flex items-center justify-between border-b border-cream/10 px-6 py-5">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-kwila">Mr Kwila</p>
                <h2 className="font-display text-2xl">Your Quote List</h2>
              </div>
              <button
                data-testid="quote-drawer-close"
                onClick={close}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 transition-colors hover:bg-cream hover:text-ink"
                aria-label="Close quote list"
              >
                <X size={17} />
              </button>
            </div>

            {done ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center" data-testid="quote-success">
                <p className="font-display text-4xl">Thank you.</p>
                <p className="text-sm text-cream/70">
                  Your quote request has been received. Our team will be in touch with pricing, lead times and freight options.
                </p>
                <button
                  data-testid="quote-success-close"
                  onClick={close}
                  className="mt-2 rounded-full bg-kwila px-8 py-3 text-xs font-bold uppercase tracking-[0.14em]"
                >
                  Continue Browsing
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto">
                  <div className="px-6 py-4">
                  {items.length === 0 ? (
                    <p className="py-10 text-center text-sm text-cream/50" data-testid="quote-empty-message">
                      Your quote list is empty. Browse the shop and add products to request pricing.
                    </p>
                  ) : (
                    <ul className="divide-y divide-cream/10" data-testid="quote-items-list">
                      {items.map((i) => (
                        <li key={i.id} className="flex gap-4 py-4" data-testid={`quote-item-${i.slug}`}>
                          <img src={i.image} alt={i.name} className="h-16 w-16 rounded-sm object-cover" />
                          <div className="flex-1">
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cream/50">
                              {i.division} · {i.category}
                            </p>
                            <p className="font-display text-lg leading-snug">{i.name}</p>
                            <div className="mt-2 flex items-center gap-3">
                              <button
                                data-testid={`quote-qty-minus-${i.slug}`}
                                onClick={() => setQty(i.id, i.qty - 1)}
                                className="flex h-7 w-7 items-center justify-center rounded-full border border-cream/25"
                                aria-label="Decrease quantity"
                              >
                                <Minus size={12} />
                              </button>
                              <span className="w-6 text-center text-sm font-bold">{i.qty}</span>
                              <button
                                data-testid={`quote-qty-plus-${i.slug}`}
                                onClick={() => setQty(i.id, i.qty + 1)}
                                className="flex h-7 w-7 items-center justify-center rounded-full border border-cream/25"
                                aria-label="Increase quantity"
                              >
                                <Plus size={12} />
                              </button>
                            </div>
                          </div>
                          <button
                            data-testid={`quote-remove-${i.slug}`}
                            onClick={() => remove(i.id)}
                            className="self-start text-cream/40 transition-colors hover:text-kwila"
                            aria-label={`Remove ${i.name}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {items.length > 0 && (
                  <form onSubmit={submit} className="space-y-3 border-t border-cream/10 px-6 py-5" data-testid="quote-form">
                    <div className="grid grid-cols-2 gap-3">
                      <input data-testid="quote-name-input" required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} />
                      <input data-testid="quote-email-input" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                      <input data-testid="quote-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                      <input data-testid="quote-postcode-input" placeholder="Postcode" value={form.postcode} onChange={set("postcode")} className={inputCls} />
                    </div>
                    <select data-testid="quote-buyer-type-select" value={form.buyer_type} onChange={set("buyer_type")} className={`${inputCls} bg-ink`}>
                      {BUYER_TYPES.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    <textarea data-testid="quote-message-input" rows={2} placeholder="Delivery location, sizes, timing…" value={form.message} onChange={set("message")} className={inputCls} />
                    <button
                      data-testid="quote-submit-button"
                      disabled={busy}
                      className="w-full rounded-full bg-kwila py-3.5 text-xs font-bold uppercase tracking-[0.16em] transition-colors hover:bg-cream hover:text-ink disabled:opacity-50"
                    >
                      {busy ? "Sending…" : `Submit Quote Request (${items.length} item${items.length > 1 ? "s" : ""})`}
                    </button>
                  </form>
                )}
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
